import type { MobileStripVariantConfig } from "../stripVariants"
import type { StripPlotArea } from "../stripLayout"
import {
  lerpNumber,
  sampleSeriesAt,
  type TimeValuePoint,
} from "../interpolate"
import { DESKTOP_VIEW_Y_MIN, type StripCamera } from "./useStripCamera"

type YScaleInputs = {
  isDesktopMirror: boolean
  config: MobileStripVariantConfig
  plot: StripPlotArea
  camera: StripCamera
  currentT: number
  linePoints: TimeValuePoint[]
  lineValueAt: (time: number) => number
  boundsMaxSeries: TimeValuePoint[]
  bandTopValueAt: (t: number) => number
}

// value → screen y for the strip's current frame.
export const buildStripYForValue = (
  inputs: YScaleInputs,
): ((value: number) => number) =>
  inputs.isDesktopMirror ? desktopBlendedAxis(inputs) : squishLogAxis(inputs)

// Two fixed axis mappings — the pre-choice LINEAR close-up and the
// choice-point LOG fan — blended in SCREEN space by choiceBlend: the
// axis types can't be interpolated on one value function, but their
// screen outputs can, and each closure uses its OWN frame's params so
// both endpoints stay stable and the lerp is a clean straight-line move.
const desktopBlendedAxis = (inputs: YScaleInputs) => {
  const linearY = preChoiceLinearAxis(inputs)
  const logY = fanLogAxis(inputs)
  const blend = inputs.camera.choiceBlend
  if (blend <= 0) return linearY
  if (blend >= 1) return logY
  return (value: number) => lerpNumber(linearY(value), logY(value), blend)
}

// The linear close-up. NOT clamped: at the 2029-2030 beat the line is
// DESIGNED to outgrow the frame and exit the top (the svg clips it).
// Uses the pre-blend framedYMax — the linear endpoint must hold still
// while the fan blend runs, or it would chase the fan and double-count.
const preChoiceLinearAxis = ({ config, plot, camera, bandTopValueAt }: YScaleInputs) => {
  const yMax = config.yMaxMode === "band-top"
    ? bandTopValueAt(camera.anchorT) * config.yHeadroomFactor
    : camera.framedYMax
  const ySpan = yMax - DESKTOP_VIEW_Y_MIN || 1
  return (value: number) =>
    plot.padding.top + plot.innerHeight
      - ((value - DESKTOP_VIEW_Y_MIN) / ySpan) * plot.innerHeight
}

// The fan's LOG axis (1.1 floor → the keyframed fan yMax): the plans'
// exponential curves fan apart as gentle climbs across the strip height
// — S flat along the floor, A arcing through the middle, B/C/D peeling
// off the top. On the desktop's linear fan axis every plan glues to the
// baseline until ~2031 then spikes vertically, unreadable at 76px.
const fanLogAxis = ({ plot, camera }: YScaleInputs) => {
  const logMin = Math.log(DESKTOP_VIEW_Y_MIN)
  const logSpan = Math.log(camera.choicePointFrame.yMax) - logMin || 1
  return (value: number) =>
    plot.padding.top + plot.innerHeight
      - ((Math.log(Math.max(value, DESKTOP_VIEW_Y_MIN)) - logMin) / logSpan)
        * plot.innerHeight
}

// squish: log axis fit from the revealed line's floor up to the honest
// loss-of-control max (or the band top), plus headroom.
const squishLogAxis = (inputs: YScaleInputs) => {
  const { config, plot, currentT } = inputs
  const logMin = Math.log(minRevealedLineValue(inputs))
  const yTopValue = config.yMaxMode === "loc-max"
    ? sampleSeriesAt(inputs.boundsMaxSeries, currentT)
    : inputs.bandTopValueAt(currentT)
  const logSpan = Math.log(yTopValue * config.yHeadroomFactor) - logMin || 1
  return (value: number) =>
    plot.padding.top + plot.innerHeight
      - ((Math.log(value) - logMin) / logSpan) * plot.innerHeight
}

// Smallest value on the revealed line, the current date included.
const minRevealedLineValue = ({ linePoints, currentT, lineValueAt }: YScaleInputs) => {
  let min = lineValueAt(currentT)
  let sawRevealed = false
  for (const point of linePoints) {
    if (point.time >= currentT) continue
    min = Math.min(min, point.value)
    sawRevealed = true
  }
  return sawRevealed ? min : Math.min(min, linePoints[0]?.value ?? 1)
}
